import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { authService } from '@/services/authService'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Lock, Mail, User, CreditCard, Smartphone } from 'lucide-react'

import { getAuthErrorMessage } from '@/utils/authErrors'

export default function SignUpPage() {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    cpf: '',
    phone: '',
    identificationCode: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const maskCPF = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1')
  }

  const maskPhone = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem')
      setLoading(false)
      return
    }

    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres')
      setLoading(false)
      return
    }

    try {
      const user = await authService.signUp(
        formData.email,
        formData.password,
        formData.displayName
      )

      if (!user || !user.uid) {
        throw new Error('Erro ao criar usuário')
      }

      await setDoc(doc(db, 'users', user.uid), {
        id: user.uid,
        displayName: formData.displayName,
        email: formData.email,
        cpf: formData.cpf,
        phone: formData.phone,
        identificationCode: formData.identificationCode,
        currentOrgId: '',
        createdAt: serverTimestamp(),
      })

      toast.success('Seja bem-vindo ao SpaceRub!')
      navigate('/onboarding')
    } catch (error: any) {
      console.error('Erro ao cadastrar:', error)
      const message = getAuthErrorMessage(error.code)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-md w-full animate-in fade-in zoom-in duration-500">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 relative overflow-hidden">
          {/* Decorative element */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/5 rounded-bl-[5rem] -mr-16 -mt-16"></div>

          <div className="flex flex-col items-center mb-6">
            <div className="w-36 h-36 overflow-hidden">
              <img src="https://apprub.com.br/space/logo.png" className="w-full h-full object-contain" alt="SpaceRub Logo" />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <button
              onClick={async () => {
                setLoading(true)
                try {
                  const user = await authService.signInWithGoogle()
                  const userDoc = await authService.getUserData(user.uid)

                  if (!userDoc) {
                    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore')
                    const { db } = await import('@/config/firebase')

                    await setDoc(doc(db, 'users', user.uid), {
                      id: user.uid,
                      displayName: user.displayName || 'Usuário Google',
                      email: user.email,
                      photoUrl: user.photoURL,
                      createdAt: serverTimestamp(),
                    })
                  }

                  toast.success('Conta criada com Google!')
                  navigate('/onboarding')
                } catch (error: any) {
                  console.error(error)
                  toast.error('Erro no cadastro Google')
                } finally {
                  setLoading(false)
                }
              }}
              type="button"
              disabled={loading}
              className="w-full bg-white border-2 border-slate-100 hover:bg-slate-50 text-slate-600 font-bold py-4 rounded-2xl flex items-center justify-center space-x-3 transition-all"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span>Cadastrar com Google</span>
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 text-xs font-bold uppercase">Ou cadastre com e-mail</span>
              </div>
            </div>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nome Completo</label>
              <div className="relative group">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type="text"
                  required
                  className="w-full bg-gray-50 border-2 border-slate-200 focus:bg-white focus:border-primary rounded-2xl pl-12 pr-4 py-4 text-sm transition-all outline-none font-medium"
                  placeholder="Como devemos te chamar?"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">CPF</label>
              <div className="relative group">
                <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type="text"
                  required
                  className="w-full bg-gray-50 border-2 border-slate-200 focus:bg-white focus:border-primary rounded-2xl pl-12 pr-4 py-4 text-sm transition-all outline-none font-medium"
                  placeholder="000.000.000-00"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: maskCPF(e.target.value) })}
                  maxLength={14}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Celular</label>
              <div className="relative group">
                <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type="tel"
                  required
                  className="w-full bg-gray-50 border-2 border-slate-200 focus:bg-white focus:border-primary rounded-2xl pl-12 pr-4 py-4 text-sm transition-all outline-none font-medium"
                  placeholder="(00) 00000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: maskPhone(e.target.value) })}
                  maxLength={15}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">E-mail</label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type="email"
                  required
                  className="w-full bg-gray-50 border-2 border-slate-200 focus:bg-white focus:border-primary rounded-2xl pl-12 pr-4 py-4 text-sm transition-all outline-none font-medium"
                  placeholder="exemplo@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Senha</label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full bg-gray-50 border-2 border-slate-200 focus:bg-white focus:border-primary rounded-2xl pl-12 pr-12 py-4 text-sm transition-all outline-none font-medium"
                  placeholder="Mínimo 6 caracteres"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Confirmar Senha</label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-primary transition-colors" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  className="w-full bg-gray-50 border-2 border-slate-200 focus:bg-white focus:border-primary rounded-2xl pl-12 pr-12 py-4 text-sm transition-all outline-none font-medium"
                  placeholder="Repita sua senha"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d4a94d] hover:bg-[#001c28] text-white font-black py-5 rounded-2xl shadow-xl shadow-[#d4a94d]/20 hover:shadow-[#001c28]/40 transform active:scale-[0.98] transition-all uppercase tracking-widest text-xs disabled:opacity-50 mt-4"
            >
              {loading ? 'Criando sua conta...' : 'Cadastrar agora'}
            </button>
          </form>

          <div className="mt-4 pt-4 border-t border-gray-50 text-center">
            <p className="text-sm text-gray-500 font-medium leading-relaxed">
              Já faz parte do SpaceRub?{' '}
              <Link to="/login" className="text-primary font-black hover:text-secondary hover:underline uppercase tracking-tight italic transition-colors">
                Entrar na conta
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
